import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { To } from 'react-router-dom';
import { Typography } from '~/components/Typography';
import { CardActionsTitleText } from '~/components/cards';
import type { PlaylistList__Playlist as Playlist } from '../../queries/queries.generated';
import {
  PlaylistCardChildren,
  PlaylistCardContainer,
  PlaylistCardIconContainer,
  PlaylistCardLink,
  PlaylistCardMetaContainer,
  PlaylistCardNotLink,
} from '../lib/styles';
import { PlaylistThumbnail } from './PlaylistThumbnail';

type OptionalLinkProps = Pick<PlaylistCardProps, 'children' | 'to'>;

const OptionalLink = ({ children, to }: OptionalLinkProps) =>
  to ? (
    <PlaylistCardLink to={to}>{children}</PlaylistCardLink>
  ) : (
    <PlaylistCardNotLink>{children}</PlaylistCardNotLink>
  );

export interface PlaylistCardProps {
  children?: ReactNode;
  playlist: Playlist;
  to?: To;
}

export const PlaylistCard = ({ children, playlist, to }: PlaylistCardProps) => {
  const { t } = useTranslation();
  const itemCount = playlist.playlistItemsCount;

  return (
    <PlaylistCardContainer>
      <OptionalLink to={to}>
        <PlaylistCardIconContainer>
          <PlaylistThumbnail size="small" />
        </PlaylistCardIconContainer>
        <PlaylistCardMetaContainer>
          <CardActionsTitleText title={playlist.name} />
          <Typography color="text.secondary" variant="subtitle2">
            {itemCount} {t('common.pluralize.item_plural', { count: itemCount })}
          </Typography>
        </PlaylistCardMetaContainer>
      </OptionalLink>
      <PlaylistCardChildren>{children}</PlaylistCardChildren>
    </PlaylistCardContainer>
  );
};
